import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import type { Exercise, NewExercise } from "../../../shared/src";
import {
  ApiError,
  archiveExercise,
  createExercise,
  listExercises,
  unarchiveExercise,
  updateExercise,
} from "../api-client";

const EXERCISES_KEY = ["exercises"] as const;

interface FormState {
  name: string;
  muscle_group: string;
  notes: string;
}

const EMPTY_FORM: FormState = { name: "", muscle_group: "", notes: "" };

function toNewExercise(form: FormState): NewExercise {
  return {
    name: form.name.trim(),
    muscle_group: form.muscle_group.trim() || null,
    notes: form.notes.trim() || null,
  };
}

function exerciseToForm(e: Exercise): FormState {
  return {
    name: e.name,
    muscle_group: e.muscle_group ?? "",
    notes: e.notes ?? "",
  };
}

export function Exercises() {
  const queryClient = useQueryClient();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [editError, setEditError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: [...EXERCISES_KEY, { includeArchived }],
    queryFn: () => listExercises({ includeArchived }),
  });

  function invalidateList() {
    return queryClient.invalidateQueries({ queryKey: EXERCISES_KEY });
  }

  const createMutation = useMutation({
    mutationFn: (input: NewExercise) => createExercise(input),
    onSuccess: () => invalidateList(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<NewExercise> }) =>
      updateExercise(id, patch),
    onSuccess: () => invalidateList(),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => archiveExercise(id),
    onSuccess: () => invalidateList(),
  });

  const unarchiveMutation = useMutation({
    mutationFn: (id: number) => unarchiveExercise(id),
    onSuccess: () => invalidateList(),
  });

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (form.name.trim().length === 0) return;
    setCreateError(null);
    try {
      await createMutation.mutateAsync(toNewExercise(form));
      setForm(EMPTY_FORM);
    } catch (err) {
      setCreateError(messageFor(err));
    }
  }

  function startEdit(ex: Exercise) {
    setEditingId(ex.id);
    setEditForm(exerciseToForm(ex));
    setEditError(null);
  }

  async function onSaveEdit(e: FormEvent) {
    e.preventDefault();
    if (editingId === null) return;
    setEditError(null);
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        patch: toNewExercise(editForm),
      });
      setEditingId(null);
    } catch (err) {
      setEditError(messageFor(err));
    }
  }

  return (
    <main className="min-h-dvh p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Exercises</h1>
        <a href="/dashboard" className="text-sm text-slate-400 hover:text-slate-200">
          ← Dashboard
        </a>
      </header>

      <form
        onSubmit={onCreate}
        className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3"
        aria-label="new exercise"
      >
        <h2 className="text-sm font-medium text-slate-300">New exercise</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-slate-400">Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={100}
              aria-label="name"
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Muscle group</span>
            <input
              type="text"
              value={form.muscle_group}
              onChange={(e) =>
                setForm({ ...form, muscle_group: e.target.value })
              }
              maxLength={50}
              aria-label="muscle group"
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-slate-400">Notes</span>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            maxLength={1000}
            rows={2}
            aria-label="notes"
            className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
          />
        </label>
        {createError && (
          <p className="text-sm text-red-400" role="alert">
            {createError}
          </p>
        )}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={
              createMutation.isPending || form.name.trim().length === 0
            }
            className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
          >
            {createMutation.isPending ? "Adding…" : "Add exercise"}
          </button>
        </div>
      </form>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-slate-300">
            {listQuery.data ? `${listQuery.data.length} exercises` : "Loading…"}
          </h2>
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>

        {listQuery.isError && (
          <p className="text-sm text-red-400" role="alert">
            Failed to load exercises.
          </p>
        )}

        {listQuery.data && listQuery.data.length === 0 && (
          <p className="text-sm text-slate-400">
            No exercises yet. Add one above to get started.
          </p>
        )}

        <ul className="space-y-2">
          {listQuery.data?.map((ex) => (
            <li
              key={ex.id}
              className="rounded-md border border-slate-800 bg-slate-900"
            >
              {editingId === ex.id ? (
                <form
                  onSubmit={onSaveEdit}
                  className="p-3 space-y-3"
                  aria-label={`edit ${ex.name}`}
                >
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                    required
                    maxLength={100}
                    aria-label="name"
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
                  />
                  <input
                    type="text"
                    value={editForm.muscle_group}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        muscle_group: e.target.value,
                      })
                    }
                    maxLength={50}
                    aria-label="muscle group"
                    placeholder="muscle group"
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
                  />
                  <textarea
                    value={editForm.notes}
                    onChange={(e) =>
                      setEditForm({ ...editForm, notes: e.target.value })
                    }
                    maxLength={1000}
                    rows={2}
                    aria-label="notes"
                    placeholder="notes"
                    className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base"
                  />
                  {editError && (
                    <p className="text-sm text-red-400" role="alert">
                      {editError}
                    </p>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateMutation.isPending}
                      className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
                    >
                      Save
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between gap-3 p-3">
                  <button
                    type="button"
                    onClick={() => startEdit(ex)}
                    className="flex-1 text-left"
                  >
                    <div className="text-base font-medium">
                      {ex.name}
                      {ex.archived && (
                        <span className="ml-2 text-xs text-slate-500">
                          (archived)
                        </span>
                      )}
                    </div>
                    {ex.muscle_group && (
                      <div className="text-xs text-slate-400">
                        {ex.muscle_group}
                      </div>
                    )}
                  </button>
                  <Link
                    to={`/exercises/${ex.id}/history`}
                    aria-label={`history for ${ex.name}`}
                    className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                  >
                    History
                  </Link>
                  {ex.archived ? (
                    <button
                      type="button"
                      onClick={() => unarchiveMutation.mutate(ex.id)}
                      disabled={unarchiveMutation.isPending}
                      className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                    >
                      Unarchive
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => archiveMutation.mutate(ex.id)}
                      disabled={archiveMutation.isPending}
                      className="rounded-md border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
                    >
                      Archive
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "CONFLICT") return "An exercise with that name already exists.";
    if (err.code === "BAD_REQUEST") return err.message;
  }
  return "Something went wrong. Try again.";
}
